import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getPayInvoice, createPayIntent, ApiError, type PayInvoiceInfo, type PaymentRail } from '../lib/api';
import { fmtCents } from '../lib/format';

let stripePromise: Promise<Stripe | null> | null = null;
const getStripe = (pk: string) => (stripePromise ??= loadStripe(pk));

type HyperInstance = {
  widgets: (o: { clientSecret: string }) => { create: (t: string, o?: unknown) => { mount: (selector: string) => void } };
  confirmPayment: (o: unknown) => Promise<{ error?: { message?: string } }>;
};
let hyperPromise: Promise<(pk: string) => HyperInstance> | null = null;
function loadHyper(): Promise<(pk: string) => HyperInstance> {
  const w = window as unknown as { Hyper?: (pk: string) => HyperInstance };
  if (w.Hyper) return Promise.resolve(w.Hyper);
  hyperPromise ??= new Promise((resolve, reject) => {
    const s = document.createElement('script');
    // Production HyperLoader. beta.hyperswitch.io is the sandbox build and
    // cannot drive a live pk_prd_ key: it crashed on the pay page with
    // "c.split is not a function" and never mounted the card form.
    s.src = 'https://checkout.hyperswitch.io/v1/HyperLoader.js';
    s.async = true;
    s.onload = () => (w.Hyper ? resolve(w.Hyper) : reject(new Error('Hyperswitch SDK unavailable')));
    s.onerror = () => reject(new Error('Failed to load Hyperswitch SDK'));
    document.head.appendChild(s);
  });
  return hyperPromise;
}

const shell: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  background: 'var(--p-background,#f6f7f9)',
  fontFamily: 'var(--font-product)',
  padding: '48px 16px',
};
const card: React.CSSProperties = {
  width: 460,
  maxWidth: '100%',
  background: '#fff',
  border: '1px solid var(--p-border)',
  borderRadius: 12,
  boxShadow: 'var(--p-shadow-lg)',
  padding: 28,
};

export const PayPage = () => {
  const { token } = useParams();
  const [info, setInfo] = useState<PayInvoiceInfo | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [intent, setIntent] = useState<{ clientSecret: string; publishableKey: string | null; rail: PaymentRail } | null>(null);
  const [starting, setStarting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    getPayInvoice(token)
      .then((r) => setInfo(r.invoice))
      .catch((e) => setLoadErr(e instanceof ApiError ? e.message : 'Invoice not found'));
  }, [token]);

  async function start() {
    if (!token) return;
    setStarting(true);
    setLoadErr(null);
    try {
      setIntent(await createPayIntent(token));
    } catch (e) {
      setLoadErr(e instanceof ApiError ? e.message : 'Could not start payment');
    } finally {
      setStarting(false);
    }
  }

  if (loadErr && !info) return <div style={shell}><div style={card}><h2 style={{ margin: 0 }}>Invoice unavailable</h2><p style={{ color: 'var(--p-muted-fg)' }}>{loadErr}</p></div></div>;
  if (!info) return <div style={shell}><div style={card}>Loading…</div></div>;

  return (
    <div style={shell}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <img src="/assets/5axis-icon.svg" alt="5 Axis" style={{ width: 26, height: 26 }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>5 Axis Health</div>
            <div style={{ fontSize: 12, color: 'var(--p-muted-fg)' }}>Invoice {info.invoiceNumber ?? ''}</div>
          </div>
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: 'var(--p-muted-fg)' }}>Amount due</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{fmtCents(info.amountDueCents)}</div>
          </div>
        </div>

        <div style={{ fontSize: 13, color: 'var(--p-muted-fg)', marginBottom: 12 }}>
          Billed to <strong style={{ color: 'var(--p-foreground)' }}>{info.clientName}</strong> · {info.period}
        </div>

        <table className="p-table" style={{ marginBottom: 16 }}>
          <tbody>
            {info.lineItems.map((li, i) => (
              <tr key={i}>
                <td>{li.description}</td>
                <td className="tabular" style={{ textAlign: 'right' }}>{fmtCents(li.amountCents)}</td>
              </tr>
            ))}
            <tr style={{ borderTop: '2px solid var(--p-border)' }}>
              <td style={{ fontWeight: 700 }}>Total</td>
              <td className="tabular" style={{ textAlign: 'right', fontWeight: 700 }}>{fmtCents(info.totalCents)}</td>
            </tr>
          </tbody>
        </table>

        {done ? (
          <div style={{ padding: '14px', borderRadius: 8, background: 'rgba(21,128,61,.08)', color: 'var(--status-success-ink,#15803d)', fontWeight: 600 }}>
            ✓ Payment received — thank you.
          </div>
        ) : info.status === 'paid' ? (
          <div style={{ color: 'var(--status-success-ink,#15803d)', fontWeight: 600 }}>This invoice is already paid.</div>
        ) : !info.payable ? (
          <div style={{ color: 'var(--p-muted-fg)' }}>This invoice can’t be paid online (status: {info.status}).</div>
        ) : !intent ? (
          <>
            {loadErr && <div style={{ marginBottom: 10, color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>{loadErr}</div>}
            <button className="btn btn--primary" disabled={starting} onClick={start} style={{ width: '100%' }}>
              {starting ? 'Preparing…' : `Pay ${fmtCents(info.amountDueCents)} by card`}
            </button>
          </>
        ) : !intent.publishableKey ? (
          <div style={{ color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>Card payments aren’t configured.</div>
        ) : intent.rail === 'stripe' ? (
          <Elements stripe={getStripe(intent.publishableKey)} options={{ clientSecret: intent.clientSecret, appearance: { theme: 'stripe' } }}>
            <StripePay onDone={() => setDone(true)} />
          </Elements>
        ) : (
          <HyperPay clientSecret={intent.clientSecret} publishableKey={intent.publishableKey} onDone={() => setDone(true)} />
        )}
      </div>
    </div>
  );
};

function StripePay({ onDone }: { onDone: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError(null);
    const { error: err } = await stripe.confirmPayment({ elements, redirect: 'if_required' });
    setBusy(false);
    if (err) setError(err.message ?? 'Payment failed');
    else onDone();
  };

  return (
    <form onSubmit={submit}>
      <PaymentElement />
      {error && <div style={{ marginTop: 10, color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>{error}</div>}
      <button type="submit" className="btn btn--primary" disabled={!stripe || busy} style={{ marginTop: 14, width: '100%' }}>
        {busy ? 'Paying…' : 'Pay now'}
      </button>
    </form>
  );
}

function HyperPay({ clientSecret, publishableKey, onDone }: { clientSecret: string; publishableKey: string; onDone: () => void }) {
  const mountRef = useRef<HTMLDivElement>(null);
  // HyperLoader's mount() resolves a CSS selector itself and calls .split on
  // what it is given — passing the DOM node threw "c.split is not a function"
  // and the card form never appeared. Stripe Elements takes a node; this does not.
  const mountId = useRef(`hyper-mount-${Math.random().toString(36).slice(2)}`).current;
  const ctx = useRef<{ hyper?: HyperInstance; widgets?: ReturnType<HyperInstance['widgets']> }>({});
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadHyper()
      .then((Hyper) => {
        if (cancelled || !mountRef.current) return;
        const hyper = Hyper(publishableKey);
        const widgets = hyper.widgets({ clientSecret });
        widgets.create('payment').mount(`#${mountId}`);
        ctx.current = { hyper, widgets };
        setReady(true);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load payment form'));
    return () => { cancelled = true; };
  }, [clientSecret, publishableKey]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const { hyper, widgets } = ctx.current;
    if (!hyper || !widgets) return;
    setBusy(true);
    setError(null);
    try {
      const res = await hyper.confirmPayment({ widgets, confirmParams: { return_url: window.location.href }, redirect: 'if_required' });
      if (res?.error) setError(res.error.message ?? 'Payment failed');
      else onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Payment failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div ref={mountRef} id={mountId} />
      {!ready && !error && <div style={{ color: 'var(--p-muted-fg)' }}>Preparing…</div>}
      {error && <div style={{ marginTop: 10, color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>{error}</div>}
      <button type="submit" className="btn btn--primary" disabled={!ready || busy} style={{ marginTop: 14, width: '100%' }}>
        {busy ? 'Paying…' : 'Pay now'}
      </button>
    </form>
  );
}
